import { Injectable, signal, computed } from '@angular/core';

export type Locale = 'en' | 'ar';

const DICT: Record<Locale, Record<string, string>> = {
  en: {
    'app.title': 'NEC Survey Admin',
    'login.title': 'Welcome to NEC Survey Admin',
    'login.subtitle': 'Please sign in with your Active Directory credentials',
    'login.usernamePlaceholder': 'Your NEC username',
    'login.passwordPlaceholder': 'Your NEC password',
    'login.submit': 'Sign in',
    'login.footer': 'All rights reserved © 2026 National Events Center',
    'login.invalidForm': 'Please fill in all required fields.',
    'login.invalidCredentials': 'Username or password is incorrect.',
    'login.failed': 'Login failed. Please try again.',
    'nav.responses': 'Responses',
    'nav.dashboard': 'Dashboard',
    'nav.systems': 'Systems',
    'nav.signOut': 'Sign out',
    'filters.system': 'System',
    'filters.topic': 'Topic',
    'filters.status': 'Status',
    'filters.ratingMin': 'Rating ≥',
    'filters.ratingMax': 'Rating ≤',
    'filters.from': 'From',
    'filters.to': 'To',
    'filters.search': 'Search',
    'filters.apply': 'Apply',
    'filters.export': 'Export',
    'tbl.rating': 'Rating',
    'tbl.user': 'User',
    'tbl.email': 'Email',
    'tbl.source': 'Source',
    'tbl.topic': 'Topic',
    'tbl.status': 'Status',
    'tbl.created': 'Date',
    'tbl.empty': 'No responses yet.',
    'kpi.total': 'Total responses',
    'kpi.avg': 'Average rating',
    'kpi.distribution': 'Distribution',
    'kpi.byTopic': 'By topic',
    'kpi.byStatus': 'By status',
    'kpi.bySource': 'By source',
    'systems.create': 'Add system',
    'systems.rotate': 'Rotate key',
    'systems.name': 'Name',
    'systems.active': 'Active',
    'systems.allowAnon': 'Allow anonymous',
    'systems.created': 'Created',
    'systems.rotated': 'Last rotated',
    'nav.users': 'Users',
    'users.subject': 'Subject',
    'users.email': 'Email',
    'users.displayName': 'Display name',
    'users.active': 'Active',
    'users.created': 'Created',
    'users.create': 'Add user',
    'users.deactivate': 'Deactivate',
    'users.activate': 'Activate',
    'users.createdMessage': 'User created.',
    'users.createFailed': 'Failed to create user.',
  },
  ar: {
    'app.title': 'لوحة الملاحظات - NEC',
    'login.title': 'مرحبا بك في لوحة تحكم',
    'login.subtitle': 'من فضلك .. أدخل بياناتك',
    'login.usernamePlaceholder': 'Youremail@nec.gov.sa',
    'login.passwordPlaceholder': 'Your NEC account password',
    'login.submit': 'تسجيل الدخول',
    'login.footer': 'جميع الحقوق محفوظة © 2026 المركز الوطني للفعاليات',
    'login.invalidForm': 'يرجى ملء جميع الحقول المطلوبة.',
    'login.invalidCredentials': 'اسم المستخدم أو كلمة المرور غير صحيحة.',
    'login.failed': 'فشل تسجيل الدخول. حاول مرة أخرى.',
    'nav.responses': 'الردود',
    'nav.dashboard': 'لوحة المعلومات',
    'nav.systems': 'الأنظمة',
    'nav.signOut': 'تسجيل خروج',
    'filters.system': 'النظام',
    'filters.topic': 'الموضوع',
    'filters.status': 'الحالة',
    'filters.ratingMin': 'تقييم ≥',
    'filters.ratingMax': 'تقييم ≤',
    'filters.from': 'من',
    'filters.to': 'إلى',
    'filters.search': 'بحث',
    'filters.apply': 'تطبيق',
    'filters.export': 'تصدير',
    'tbl.rating': 'التقييم',
    'tbl.user': 'المستخدم',
    'tbl.email': 'البريد',
    'tbl.source': 'المصدر',
    'tbl.topic': 'الموضوع',
    'tbl.status': 'الحالة',
    'tbl.created': 'التاريخ',
    'tbl.empty': 'لا توجد ملاحظات بعد.',
    'kpi.total': 'إجمالي الردود',
    'kpi.avg': 'متوسط التقييم',
    'kpi.distribution': 'التوزيع',
    'kpi.byTopic': 'حسب الموضوع',
    'kpi.byStatus': 'حسب الحالة',
    'kpi.bySource': 'حسب المصدر',
    'systems.create': 'إضافة نظام',
    'systems.rotate': 'تدوير المفتاح',
    'systems.name': 'الاسم',
    'systems.active': 'نشط',
    'systems.allowAnon': 'السماح بالمجهول',
    'systems.created': 'تاريخ الإنشاء',
    'systems.rotated': 'آخر تدوير',
    'nav.users': 'المستخدمون',
    'users.subject': 'المعرف',
    'users.email': 'البريد',
    'users.displayName': 'الاسم الظاهر',
    'users.active': 'نشط',
    'users.created': 'تاريخ الإنشاء',
    'users.create': 'إضافة مستخدم',
    'users.deactivate': 'تعطيل',
    'users.activate': 'تفعيل',
    'users.createdMessage': 'تم إنشاء المستخدم.',
    'users.createFailed': 'فشل في إنشاء المستخدم.',
  },
};

@Injectable({ providedIn: 'root' })
export class I18n {
  private _locale = signal<Locale>((localStorage.getItem('locale') as Locale) || 'ar');

  locale = this._locale.asReadonly();
  dir = computed(() => (this._locale() === 'ar' ? 'rtl' : 'ltr'));

  setLocale(l: Locale) {
    this._locale.set(l);
    localStorage.setItem('locale', l);
    document.documentElement.lang = l;
    document.documentElement.dir = this.dir();
  }

  t(key: string): string {
    return DICT[this._locale()][key] ?? key;
  }
}
